import { useEffect, useRef } from "react";
import { RIM_COLOR } from "./Wheel";

// Flapper SVG arriba del wheel. Modelo físico realista:
//
// Cada tick (cada divisoria que pasa) SUMA un impulso al ángulo actual
// del flapper. Un loop rAF continuo aplica decay exponencial al ángulo
// frame a frame. Resultado natural:
//
//   - Alta velocidad: ticks rápidos (~30ms) sumando impulsos faster
//     que el decay → ángulo queda alto (flecha vibrando cerca del max).
//   - Baja velocidad: ticks lentos (~300ms+) → decay alcanza a llevar
//     el ángulo a 0 entre tick y tick → bumps discretos visibles.
//   - Wheel para: sin más impulsos, decay lleva ángulo a 0 → reposo.
//
// El intento previo (keyframe CSS single-shot por tick) tenía 2
// problemas: arrancaba en 0° en cada tick → a alta velocidad la flecha
// apenas se alejaba antes de reiniciarse (parecía quieta). Y el
// retorno de 28° a 0° en 90ms se sentía 'antinaturalmente rápido'.

type Props = {
  size?: number;
  /** Se incrementa cada vez que el wheel pasa un segmento. */
  bumpTick?: number;
  /** Intensidad 0..1 del tick (alta velocidad = más wobble). */
  bumpIntensity?: number;
  /** True mientras la ruleta gira. Cuando pasa a false, reset a 0°. */
  spinning?: boolean;
  /** Color base — comparte el mismo de SpinButton para que la bola
   *  decorativa de arriba y la flecha combinen con el botón central. */
  color?: string;
};

const MAX_BUMP_DEG = 40; // ángulo máximo de doblez (ampliado, antes 28)
const IMPULSE_FACTOR = 0.55; // qué % del max suma cada tick a intensity 1
const ANGLE_CLAMP_DEG = MAX_BUMP_DEG * 1.3; // tope absoluto del ángulo
const DECAY_PER_FRAME = 0.92; // factor multiplicativo cada frame (60fps)
const EPSILON_DEG = 0.05; // umbral para considerar que está en reposo

export function Flapper({
  size = 36,
  bumpTick = 0,
  bumpIntensity = 0,
  spinning = false,
  color = "#f7c948",
}: Props) {
  // Referencias para el loop: ángulo actual + raf id + flag de spinning
  // (necesitamos leer el último valor de spinning desde el loop sin
  // que el effect se vuelva a setupear). El DOM lo actualizamos vía
  // ref para evitar 60 re-renders/seg de React.
  const flapperRef = useRef<SVGSVGElement>(null);
  const angleRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const spinningRef = useRef(spinning);
  spinningRef.current = spinning;

  function loopStep() {
    angleRef.current *= DECAY_PER_FRAME;
    if (Math.abs(angleRef.current) < EPSILON_DEG) angleRef.current = 0;
    if (flapperRef.current) {
      flapperRef.current.style.transform = `rotate(${-angleRef.current}deg)`;
    }
    // Seguir loopeando mientras gire o haya ángulo residual.
    if (spinningRef.current || angleRef.current !== 0) {
      rafRef.current = requestAnimationFrame(loopStep);
    } else {
      rafRef.current = null;
    }
  }

  // Cada tick: sumar impulso. Si el loop no está corriendo, arrancarlo.
  useEffect(() => {
    if (bumpTick === 0) return;
    const i = Math.min(1, Math.max(0.3, bumpIntensity));
    angleRef.current = Math.min(
      angleRef.current + MAX_BUMP_DEG * IMPULSE_FACTOR * i,
      ANGLE_CLAMP_DEG,
    );
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(loopStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bumpTick, bumpIntensity]);

  // Al empezar a girar, asegurar que el loop esté corriendo (puede
  // haber un caso donde spinning=true pero todavía no llegó bumpTick).
  useEffect(() => {
    if (spinning && rafRef.current === null) {
      rafRef.current = requestAnimationFrame(loopStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning]);

  // Cleanup al unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  return (
    <div
      className="pointer-events-none absolute left-1/2 z-20 -translate-x-1/2"
      style={{ top: -size * 0.4 }}
      aria-hidden
    >
      {/* Botón redondo arriba (anclaje fijo, no se mueve). Color
          sólido 2D — sin gradient esfera, igual que el SpinButton.
          La bola es muy chica para que un arco interno aporte algo;
          la deja flat con borde RIM_COLOR y glow externo. */}
      <div
        className="mx-auto rounded-full shadow-lg"
        style={{
          width: size * 0.7,
          height: size * 0.7,
          border: `2px solid ${RIM_COLOR}`,
          background: color,
          boxShadow: `0 2px 10px rgba(0,0,0,0.5), 0 0 18px ${color}cc`,
        }}
      />
      {/* Triángulo apuntando hacia abajo — actualizado vía ref por el
          loop rAF. Cada tick suma impulso al ángulo, el decay frame
          a frame lo trae de vuelta a 0. Sin CSS animation: el
          transform se aplica directo al style del DOM desde el loop.
          transformOrigin 50% 0% para que rote desde la base de la
          flecha (donde "engancha" con el clavo de la divisoria). */}
      <svg
        ref={flapperRef}
        width={size}
        height={size * 1.1}
        viewBox="0 0 24 26"
        style={{
          marginTop: -size * 0.15,
          filter: `drop-shadow(0 4px 10px ${color}99)`,
          transformOrigin: "50% 0%",
          // transform inicial: reposo. El loop lo actualiza in-place.
          transform: "rotate(0deg)",
          // Sin transition: el loop ya da la suavidad del decay.
        }}
      >
        <path
          d="M12 26 L2 4 L22 4 Z"
          fill={color}
          stroke={RIM_COLOR}
          strokeWidth={1.5}
        />
      </svg>
    </div>
  );
}
